import {
  Col,
  Row,
  SkeletonDots,
  YesObject
} from '@yes.technology/react-toolkit'
import { useEffect, useMemo, useRef, useState } from 'react'

import DetailsSection from 'component/DetailsSection'
import { ScrollToViewHandle } from 'component/DetailsSection/DetailsSection.types'
import EmptyStates from 'component/EmptyStates/EmptyStates'
import { useIsOpen } from 'shared/hooks'
import useDeviceType from 'shared/hooks/useDeviceType'
import usePreloadingImages from 'shared/hooks/usePreloadingImages/usePreloadingImages'
import { StyledGrid } from 'shared/style/grid'
import { generateSummary } from 'shared/utils/string/summary'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { DeviceType } from 'site/shared/types'
import { useSiteState } from 'siteState/shared'
import NavigationSlider from '../NavigationSlider/NavigationSlider'
import {
  DetailsContainer,
  StyledCol,
  StyledGenericObjectCard
} from './CardCarousel.styles'
import {
  CardCarouselProps,
  CardComponentProps,
  CardProps
} from './CardCarousel.types'
import CardCarouselButton, {
  getCardButtonAttribution
} from './CardCarouselButton'
import mapPageAccordingToDeviceType, {
  PAGE_SIZE
} from './helpers/mapPageAccordingToDeviceType'

const Card = ({ item, button, onReadMore, imageUrl }: CardComponentProps) => {
  const attribution = getCardButtonAttribution(button, item.uuid)

  const complement = ['link', 'public'].includes(attribution.type || '')
    ? item.complement
    : generateSummary(item.complement, 150)

  return (
    <StyledGenericObjectCard
      des={item.des}
      complement={complement}
      image={imageUrl}
      frameImage
    >
      <CardCarouselButton attribution={attribution} onReadMore={onReadMore} />
    </StyledGenericObjectCard>
  )
}

const CardCarousel = ({ props }: CardCarouselProps) => {
  const [page, setPage] = useState(0)
  const [currentItem, setCurrentItem] = useState<CardProps>()
  const [scrollToDiv, setScrollToDiv] = useState(false)
  const { isOpen, open, close } = useIsOpen(false)
  const deviceType = useDeviceType()
  const styleProps = useStyleMaps(props.styleMap)

  const deviceTypeRef = useRef<DeviceType>(deviceType)

  const detailsRef = useRef<ScrollToViewHandle>(null)

  const { title, cards, button } = props

  const pageSize = PAGE_SIZE[deviceType]

  const displayedCards = useMemo(
    () => cards?.slice(page * pageSize, page * pageSize + pageSize),
    [pageSize, cards, page]
  )

  useEffect(() => {
    if (!displayedCards?.[0]) return
    setCurrentItem(displayedCards?.[0])
  }, [page, displayedCards])

  const handlePrevClick = () => {
    setPage((prevPage) => (prevPage === 0 ? prevPage : prevPage - 1))
  }

  const handleNextClick = () => {
    const totalPages = Math.ceil(cards.length / pageSize)

    setPage((prevPage) =>
      prevPage === totalPages - 1 ? prevPage : prevPage + 1
    )
  }

  const openModal = (index: number) => () => {
    setCurrentItem(displayedCards[index])
    setScrollToDiv(true)
    open()
  }

  const [allResultsFetched] = useSiteState('allResultsFetched')
  const isLoading = !allResultsFetched

  useEffect(() => {
    if (!detailsRef.current) return
    if (isOpen && scrollToDiv) {
      detailsRef.current.scrollToContent()
      setScrollToDiv(false)
    }
  }, [isOpen, scrollToDiv])

  const documentUUIDs = cards
    ?.map((item) => (item?.documento?.[0] as YesObject)?.uuid)
    .filter(Boolean)

  const images = usePreloadingImages({ documentUUIDs })

  useEffect(() => {
    const previousDeviceType = deviceTypeRef.current
    const currentDeviceType = deviceType

    if (deviceTypeRef.current !== deviceType) {
      const convertedPage = mapPageAccordingToDeviceType(
        page,
        previousDeviceType,
        currentDeviceType
      )

      setPage(convertedPage)
      deviceTypeRef.current = deviceType
    }
  }, [deviceType, page])

  const disabledArrow = useMemo(() => {
    if (!cards) return undefined

    const lastPage = Math.ceil(cards.length / pageSize) - 1

    if (page === 0) return 'prev'
    if (page === lastPage) return 'next'
    return undefined
  }, [page, cards, pageSize])

  if (isLoading) {
    return (
      <StyledGrid style={styleProps}>
        <Row style={{ justifyContent: 'center' }}>
          <Col lg={12}>
            <SkeletonDots />
          </Col>
        </Row>
      </StyledGrid>
    )
  }

  const disableSlider =
    displayedCards?.length === 0 || cards?.length <= pageSize

  return (
    <>
      {displayedCards?.length > 0 && (
        <div style={{ marginBottom: '16px', ...styleProps }}>
          <NavigationSlider
            title={title}
            onNextClick={handleNextClick}
            onPrevClick={handlePrevClick}
            disabled={disableSlider}
            disabledArrow={disabledArrow}
            currentIndex={page}
          />

          <StyledGrid>
            <Row style={{ justifyContent: 'center' }}>
              {displayedCards?.map((card, index) => {
                const documentUuid = (card?.documento?.[0] as YesObject)?.uuid
                const image = images?.find((image) => image?.[documentUuid])
                return (
                  <StyledCol
                    key={index}
                    lg={3}
                    md={6}
                    style={{ marginBottom: '16px' }}
                  >
                    <Card
                      item={card}
                      button={button}
                      onReadMore={openModal(index)}
                      imageUrl={image?.[documentUuid]}
                    />
                  </StyledCol>
                )
              })}
            </Row>

            {isOpen && (
              <Row style={{ justifyContent: 'center', marginTop: '16px' }}>
                <Col lg={12}>
                  <DetailsContainer>
                    <DetailsSection
                      isOpen={isOpen}
                      onClose={close}
                      des={currentItem?.des || ''}
                      complement={currentItem?.complement || ''}
                      ref={detailsRef}
                    />
                  </DetailsContainer>
                </Col>
              </Row>
            )}
          </StyledGrid>
        </div>
      )}

      {displayedCards?.length === 0 && (
        <StyledGrid style={styleProps}>
          <Row style={{ justifyContent: 'center' }}>
            <Col lg={12}>
              <EmptyStates state='no-data-to-view' showComplement={false} />
            </Col>
          </Row>
        </StyledGrid>
      )}
    </>
  )
}

export default CardCarousel
